import { Sidebar } from '@carboncopyco/editor-ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEditorTools } from '../../context';
import { useEditorLoaded } from '../../context/EditorSdkProvider';
import { EditorSidebarInspectButtons } from './EditorSidebarInspectButtons';
import {
  type ButtonConfig,
  type Tool,
  useButtonsConfig,
} from './useButtonsConfig';

export const EditorSidebar = () => {
  const isEditorLoaded = useEditorLoaded();
  const { isOpen, toggle } = useEditorTools();
  const {
    findReplace,
    variable,
    customCharts,
    navigation,
    customCode,
    transactions,
    importManager,
    spotEditsManager,
    objectsManager,
    guidanceManager,
    aiManager,
    editorPerformanceManager,
  } = useButtonsConfig();
  const flags = useFlags();

  const primaryActions = [
    aiManager,
    findReplace,
    variable,
    customCharts,
    navigation,
    guidanceManager,
  ];
  const secondaryActions = [
    customCode,
    transactions,
    ...(flags.editorObjects ? [objectsManager] : []),
    ...(flags.editorSpotEdits ? [] : [importManager]),
    ...(flags.editorPerformance ? [editorPerformanceManager] : []),
  ];
  const tertiaryActions = flags.editorSpotEdits
    ? [importManager, spotEditsManager]
    : [];

  const renderButton = <T extends Tool>({
    tool,
    hide,
    'aria-label': ariaLabel,
    icon,
    tooltipMessage,
    hasBadge,
  }: ButtonConfig<T>) =>
    hide ? null : (
      <Sidebar.Button
        key={tool}
        disabled={!isEditorLoaded}
        aria-label={ariaLabel}
        onClick={() => toggle(tool)}
        selected={isOpen(tool)}
        icon={icon}
        tooltipMessage={tooltipMessage}
        hasBadge={hasBadge}
      />
    );

  return (
    <Sidebar>
      <EditorSidebarInspectButtons />
      <Sidebar.Separator />
      {primaryActions.map(renderButton)}
      {secondaryActions.map(({ hide }) => !hide).some(Boolean) && (
        <Sidebar.Separator />
      )}
      {secondaryActions.map(renderButton)}
      {tertiaryActions.map(({ hide }) => !hide).some(Boolean) && (
        <Sidebar.Separator className="mt-auto" />
      )}
      {tertiaryActions.map(renderButton)}
    </Sidebar>
  );
};
